import React, { useCallback, useState, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertCircle, X } from 'lucide-react';
type ToastType = 'success' | 'error' | 'warning';
interface Toast {
  id: string;
  type: ToastType;
  message: string;
}
interface ToastContextType {
  showToast: (type: ToastType, message: string) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const showToast = useCallback((type: ToastType, message: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, {
      id,
      type,
      message
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  }, []);
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };
  return <ToastContext.Provider value={{
    showToast
  }} data-id="element-725">
      {children}
      <div className="fixed top-4 right-4 z-[9999] space-y-2 max-w-md" data-id="element-726">
        <AnimatePresence data-id="element-727">
          {toasts.map(toast => <motion.div key={toast.id} initial={{
          opacity: 0,
          y: -20,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          x: 100,
          scale: 0.95
        }} className={`flex items-start gap-3 p-4 rounded-xl shadow-lg backdrop-blur-sm border ${toast.type === 'success' ? 'bg-green-50/90 border-green-200 text-green-800' : toast.type === 'error' ? 'bg-red-50/90 border-red-200 text-red-800' : 'bg-yellow-50/90 border-yellow-200 text-yellow-800'}`} data-id="element-728">
              {toast.type === 'success' && <CheckCircle2 className="w-5 h-5 flex-shrink-0" data-id="element-729" />}
              {toast.type === 'error' && <XCircle className="w-5 h-5 flex-shrink-0" data-id="element-730" />}
              {toast.type === 'warning' && <AlertCircle className="w-5 h-5 flex-shrink-0" data-id="element-731" />}

              <p className="flex-1 text-sm font-medium" data-id="element-732">{toast.message}</p>

              <button onClick={() => removeToast(toast.id)} className="flex-shrink-0 hover:opacity-70 transition-opacity" data-id="element-733">
                <X className="w-4 h-4" data-id="element-734" />
              </button>
            </motion.div>)}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>;
}
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}